import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';
import { trackWhatsAppClick } from '../utils/tracking';

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();

  const handleWhatsAppClick = () => {
    trackWhatsAppClick();
    const phoneNumber = settings?.whatsapp_number || process.env.REACT_APP_WHATSAPP_NUMBER || '5511999999999';
    const message = encodeURIComponent('Olá, Dra. Luara! Gostaria de agendar um atendimento.');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero flex items-center pt-28 pb-16 lg:pt-32 lg:pb-20 min-h-[85vh]">
      <div className="hero-inner">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <h1 className="sr-only">Advocacia Trabalhista e Condominial em Jundiaí - Luara Rezende</h1>
            <div className="mb-4">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading text-brand-slate font-bold leading-tight"
              >
                {t('hero.headline')}
              </motion.h2>
            </div>
            
            <p className="hero-sub mb-6 text-lg">
              {t('hero.subtext')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button
                className="btn-primary text-lg px-8 py-4"
                onClick={handleWhatsAppClick}
              >
                <MessageCircle size={20} />
                {t('hero.ctaPrimary')}
              </button>
              
              <button
                className="btn-secondary text-lg px-8 py-4"
                onClick={() => scrollToSection('how-we-help')}
              >
                {t('hero.ctaSecondary')}
                <ArrowRight size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <span className="badge">
                {t('hero.region')}
              </span>
            </div>
            
            <p className="text-sm text-brand-ink/70 italic">
              {t('hero.trust')}
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="w-80 h-96 lg:w-96 lg:h-[500px] card overflow-hidden">
                <img 
                  src="/images/hero.jpg" 
                  alt="Luara Rezende Advocacia"
                  fetchPriority="high"
                  loading="eager"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-brand-accent rounded-full opacity-80"></div>
              <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-brand-slate rounded-full opacity-80"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
